import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, BookOpen, ArrowLeft, ArrowRight, X } from "lucide-react";
import {
  DOCS,
  getActiveCategories,
  getDocsForAudience,
  getDocsByCategory,
  getDocById,
  getCategory,
  searchDocs,
  buildHighlightRegex,
  type DocEntry,
} from "@/content/docs";
import { MarkdownArticle } from "@/components/docs/MarkdownArticle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

type DocsSearch = { doc?: string; cat?: string; q?: string };

export const Route = createFileRoute("/admin/docs")({
  validateSearch: (s: Record<string, unknown>): DocsSearch => ({
    doc: typeof s.doc === "string" ? s.doc : undefined,
    cat: typeof s.cat === "string" ? s.cat : undefined,
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  component: DocsPage,
});

function DocsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [query, setQuery] = useState(search.q ?? "");
  const activeCategory = search.cat ?? null;
  const audience = "admin" as const;

  const allDocs = useMemo(() => getDocsForAudience(audience), []);
  const categories = useMemo(() => getActiveCategories(audience), []);

  const activeDoc = search.doc ? getDocById(search.doc) : null;

  const trimmed = query.trim();
  const searchResults = useMemo(
    () => (trimmed.length >= 2 ? searchDocs(trimmed, allDocs) : []),
    [trimmed, allDocs],
  );
  const highlight = useMemo(
    () =>
      trimmed.length >= 2
        ? buildHighlightRegex(trimmed.toLowerCase().split(/[^a-z0-9]+/i).filter((t: string) => t.length >= 2))
        : null,
    [trimmed],
  );

  const browseList = useMemo(() => {
    if (trimmed.length >= 2) return searchResults.map((r) => r.doc);
    if (activeCategory) return getDocsByCategory(activeCategory, audience);
    return allDocs;
  }, [trimmed, searchResults, activeCategory, allDocs]);

  function selectDoc(id: string) {
    navigate({ search: (prev: DocsSearch) => ({ ...prev, doc: id }) });
  }
  function clearDoc() {
    navigate({ search: (prev: DocsSearch) => ({ ...prev, doc: undefined }) });
  }
  function toggleCategory(catId: string) {
    navigate({
      search: (prev: DocsSearch) => ({
        ...prev,
        cat: prev.cat === catId ? undefined : catId,
        doc: undefined,
      }),
    });
  }
  function commitQuery(v: string) {
    setQuery(v);
    navigate({ search: (prev: DocsSearch) => ({ ...prev, q: v.trim() || undefined, doc: undefined }) });
  }

  if (activeDoc) {
    return <ArticleView doc={activeDoc} onBack={clearDoc} />;
  }

  return (
    <div className="space-y-6 max-w-full">
      <header>
        <p className="overline mb-2">Documentation</p>
        <h2 className="font-serif text-2xl text-primary">Administrator help</h2>
        <p className="text-ink-secondary text-sm mt-2 max-w-2xl">
          Search by keyword or filter by category. Every operational decision worth remembering should be
          recorded here.
        </p>
      </header>

      <div className="flex gap-2 items-stretch">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-tertiary" />
          <Input
            value={query}
            onChange={(e) => commitQuery(e.target.value)}
            placeholder="Search documentation…"
            className="pl-9"
          />
          {query && (
            <button
              type="button"
              onClick={() => commitQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-tertiary hover:text-primary"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <BrowseSheet
          categories={categories}
          activeCategory={activeCategory}
          onSelectCategory={(id) => toggleCategory(id)}
          onSelectDoc={selectDoc}
          audience={audience}
        />
      </div>

      {/* Category pill row — horizontal scroll on mobile, wraps on desktop */}
      <div className="-mx-1 overflow-x-auto md:overflow-visible">
        <div className="flex md:flex-wrap gap-2 px-1 snap-x snap-mandatory">
          <CategoryPill
            label="All"
            active={!activeCategory}
            onClick={() => navigate({ search: (prev: DocsSearch) => ({ ...prev, cat: undefined, doc: undefined }) })}
          />
          {categories.map((c) => (
            <CategoryPill
              key={c.id}
              label={c.title}
              active={activeCategory === c.id}
              onClick={() => toggleCategory(c.id)}
            />
          ))}
        </div>
      </div>

      <div className="border border-border bg-card divide-y divide-border">
        {browseList.length === 0 && (
          <p className="p-6 text-sm text-ink-tertiary">No docs match that search.</p>
        )}
        {browseList.map((doc) => (
          <button
            key={doc.id}
            type="button"
            onClick={() => selectDoc(doc.id)}
            className="block w-full text-left p-4 md:p-5 hover:bg-muted/50"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-serif text-base md:text-lg text-primary truncate">
                <Highlight text={doc.title} regex={highlight} />
              </p>
              <span className="shrink-0 text-[10px] font-mono uppercase tracking-wider text-ink-tertiary">
                {getCategory(doc.category)?.title ?? doc.category}
              </span>
            </div>
            {doc.keywords && doc.keywords.length > 0 && (
              <p className="text-xs text-ink-tertiary mt-1 truncate">
                {doc.keywords.join(" · ")}
              </p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function CategoryPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`snap-start shrink-0 rounded-full px-3 py-1.5 text-xs font-mono border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "border-rule text-ink-secondary hover:bg-surface"
      }`}
    >
      {label}
    </button>
  );
}

function Highlight({ text, regex }: { text: string; regex: RegExp | null }) {
  if (!regex) return <>{text}</>;
  const parts = text.split(regex);
  return (
    <>
      {parts.map((p, i) =>
        regex.test(p) ? (
          <mark key={i} className="bg-yellow-200/60 dark:bg-yellow-500/20 px-0.5">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function BrowseSheet({
  categories,
  activeCategory,
  onSelectCategory,
  onSelectDoc,
  audience,
}: {
  categories: ReturnType<typeof getActiveCategories>;
  activeCategory: string | null;
  onSelectCategory: (id: string) => void;
  onSelectDoc: (id: string) => void;
  audience: "admin";
}) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="shrink-0 gap-2">
          <BookOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Browse</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-serif text-primary">Browse all docs</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {categories.map((c) => {
            const docs = getDocsByCategory(c.id, audience);
            return (
              <section key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelectCategory(c.id);
                    setOpen(false);
                  }}
                  className={`overline ${
                    activeCategory === c.id ? "text-primary" : "text-ink-secondary hover:text-primary"
                  }`}
                >
                  {c.title}
                </button>
                <p className="text-xs text-ink-tertiary mt-1">{c.description}</p>
                <ul className="mt-2 space-y-1">
                  {docs.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onSelectDoc(d.id);
                          setOpen(false);
                        }}
                        className="text-sm font-serif text-ink-primary hover:text-primary text-left"
                      >
                        {d.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ArticleView({ doc, onBack }: { doc: DocEntry; onBack: () => void }) {
  const category = getCategory(doc.category);
  const siblings = getDocsByCategory(doc.category, "admin");
  const idx = siblings.findIndex((d) => d.id === doc.id);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  const related = useMemo(() => {
    if (!doc.keywords || doc.keywords.length === 0) return [];
    const kw = new Set(doc.keywords.map((k) => k.toLowerCase()));
    return getDocsForAudience("admin")
      .filter((d) => d.id !== doc.id && (d.keywords ?? []).some((k) => kw.has(k.toLowerCase())))
      .slice(0, 4);
  }, [doc]);

  return (
    <article className="space-y-6 max-w-full">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-mono text-ink-tertiary hover:text-primary inline-flex items-center gap-1"
      >
        <ArrowLeft className="h-3 w-3" /> All docs
      </button>

      <nav className="text-xs font-mono text-ink-tertiary flex flex-wrap items-center gap-1">
        <Link to="/admin" className="hover:text-primary">Admin</Link>
        <span>/</span>
        <Link to="/admin/docs" className="hover:text-primary">Docs</Link>
        {category && (
          <>
            <span>/</span>
            <Link
              to="/admin/docs"
              search={{ cat: category.id }}
              className="hover:text-primary"
            >
              {category.title}
            </Link>
          </>
        )}
        <span>/</span>
        <span className="text-ink-secondary truncate">{doc.title}</span>
      </nav>

      <header className="border-b border-border pb-4">
        {doc.lastUpdated && (
          <p className="font-mono text-xs text-ink-tertiary">Updated {doc.lastUpdated}</p>
        )}
      </header>

      <MarkdownArticle body={doc.content} />

      <footer className="mt-10 pt-6 border-t border-border space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {prev ? (
            <Link
              to="/admin/docs"
              search={{ doc: prev.id }}
              className="border border-border p-3 hover:bg-muted/50 group"
            >
              <span className="text-[10px] font-mono uppercase text-ink-tertiary inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Previous
              </span>
              <p className="font-serif text-sm text-primary mt-1">{prev.title}</p>
            </Link>
          ) : <div />}
          {next ? (
            <Link
              to="/admin/docs"
              search={{ doc: next.id }}
              className="border border-border p-3 hover:bg-muted/50 sm:text-right"
            >
              <span className="text-[10px] font-mono uppercase text-ink-tertiary inline-flex items-center gap-1 sm:justify-end">
                Next <ArrowRight className="h-3 w-3" />
              </span>
              <p className="font-serif text-sm text-primary mt-1">{next.title}</p>
            </Link>
          ) : <div />}
        </div>

        {related.length > 0 && (
          <section>
            <p className="overline mb-2">Related</p>
            <ul className="flex flex-wrap gap-2">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/admin/docs"
                    search={{ doc: r.id }}
                    className="text-xs font-mono px-3 py-1.5 border border-rule rounded-full hover:bg-surface text-ink-secondary hover:text-primary"
                  >
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </footer>
    </article>
  );
}
